import {
  Company,
  CompanyVerificationStage,
} from '@features/api/response/Company';
import { CompanyAuthorizedSignatory } from '@features/api/response/CompanyAuthorizedSignatory';
import { UltimateBeneficialOwner } from '@features/api/response/UltimateBeneficialOwner';
import {
  KYCVerificationStatus,
  User,
  UserType,
} from '@features/api/response/User';
import {
  TransactionStatus,
  UserTransaction,
} from '@features/api/response/UserTransactions';
import { Steps as ProgressSteps } from '@pages/Investment/Progress/Progress.types';
import { get, isEmpty } from 'lodash';
import { NavigateFunction } from 'react-router-dom';

type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: NonNullable<
    ObjectType[Key]
  > extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<NonNullable<ObjectType[Key]>>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

export interface PageMap {
  route: string;
  requiredFields: NestedKeyOf<User>[];
  navigateProps: {
    preventScrollReset?: boolean;
    state: {
      progress: number;
    };
  };
}

export const bankDetailsFields: NestedKeyOf<User>[] = [
  'bankDetails.accountHolderName',
  'bankDetails.bankName',
  'bankDetails.bankLocation',
];

export const pagesCriteriaAndNavigation: PageMap[] = [
  {
    route: 'personal-details',
    requiredFields: [
      'basicDetails.cityOfBirth',
      'basicDetails.countryOfBirth',
      'basicDetails.pep',
    ],
    navigateProps: {
      state: {
        progress: 2,
      },
    },
  },
  {
    route: 'contact-details',
    requiredFields: [
      'contactDetails.streetAddress',
      'contactDetails.city',
      'contactDetails.state',
      'contactDetails.zipCode',
      'contactDetails.country',
      'contactDetails.phoneCountryCode',
      'contactDetails.phoneNumber',
    ],
    navigateProps: {
      state: {
        progress: 3,
      },
    },
  },
  {
    route: 'tax-details',
    requiredFields: ['taxDetails.taxResidenceCountry', 'taxDetails.usTaxPayer'],
    navigateProps: {
      state: {
        progress: 4,
      },
    },
  },
  {
    route: 'bank-details',
    requiredFields: bankDetailsFields,
    navigateProps: {
      preventScrollReset: false,
      state: {
        progress: 5,
      },
    },
  },
];

export const handleNextButtonNavigation = (
  navigate: NavigateFunction,
  user?: User,
  company?: Company,
  ultimateBeneficialOwners?: UltimateBeneficialOwner[],
  signatories?: CompanyAuthorizedSignatory[],
  productId?: string,
  transactions?: UserTransaction[]
) => {
  if (!productId || !user) return;

  const navigateWithState = (
    path: string,
    state: any,
    replace = true,
    preventScrollReset: boolean | undefined = undefined
  ) => {
    const navigationObject = {
      state,
      replace,
    };
    if (preventScrollReset !== undefined) {
      Object.assign(navigationObject, {
        preventScrollReset: preventScrollReset,
      });
    }
    return navigate(
      `/investment-subscription/${productId}/${path}`,
      navigationObject
    );
  };

  const isCompanyHolder =
    user?.userType === UserType.CompanyHolder ||
    user?.userType === UserType.NomineeCompanyHolder;

  const nomineeFlowAllowed = user.nomineeFlowAllowed;
  const hasPersonalDetailsLocked = user.personalDetailsLocked;
  const hasCompletedQuestionnaire = user.completedQuestionnaire;
  const hasPendingSignatureTransaction = transactions?.some(
    (x) => x.status === TransactionStatus.PendingCustomerSignature
  );
  const hasTransactions = transactions && transactions.length > 0;
  const placeOfBirthFieldsFilled =
    user.basicDetails.cityOfBirth && user.basicDetails.countryOfBirth;
  const isUsTaxResident = user.taxDetails?.usTaxPayer;
  const isSoleAccountOwner = user.bankDetails?.soleAccountOwner;
  const hasBankDetailsFields = bankDetailsFields.every(
    (bdField) => !isEmpty(get(user, bdField))
  );
  const isKYCVerified =
    user.kycVerificationStage === KYCVerificationStatus.CompletedVerified ||
    user.kycVerificationStage ===
      KYCVerificationStatus.ThirdPartyChecksCompletedVerified;

  const isKYBVerified =
    company?.verificationStage ===
      CompanyVerificationStage.COMPLETED_VERIFIED ||
    company?.verificationStage ===
      CompanyVerificationStage.THIRD_PARTY_CHECKS_COMPLETED_VERIFIED;

  if (hasTransactions) {
    if (isCompanyHolder) {
      return () =>
        navigateWithState('progress', {
          progress: 8,
          type: 'legal_entity',
          completedSteps: [
            ProgressSteps.Details,
            ProgressSteps.IdentityVerification,
          ],
        });
    }
    
    if (!hasCompletedQuestionnaire) {
      return () =>
        navigateWithState('questionnaire', {
          progress: 6,
        });
    }

    if (hasPendingSignatureTransaction) {
      return navigateWithState('progress', {
        completedSteps: [
          ProgressSteps.Details,
          ProgressSteps.IdentityVerification,
        ],
        progress: 8,
      });
    } else {
      return () =>
        navigateWithState('investment-profile-info', {
          progress: 7,
        });
    }
  } else {
    if (isCompanyHolder || nomineeFlowAllowed) {
      if (!company)
        return () =>
          navigateWithState('progress', { progress: 2, type: 'nominee' });
      if (!ultimateBeneficialOwners?.length)
        return () => navigateWithState('ubos', { progress: 4 });
      if (!signatories?.length)
        return () =>
          navigateWithState('authorized-signatories', {
            progress: 5,
          });
      if (isKYBVerified) {
        return () =>
          navigateWithState('progress', {
            progress: 8,
            type: 'legal_entity',
            completedSteps: [
              ProgressSteps.Details,
              ProgressSteps.IdentityVerification,
            ],
          });
      } else {
        return () =>
          navigateWithState('progress', {
            progress: 6,
            type: 'legal_entity',
            completedSteps: [ProgressSteps.Details],
          });
      }
    }

    if (!placeOfBirthFieldsFilled) {
      return () => navigateWithState('profile', { progress: 1 });
    }

    if (isUsTaxResident) {
      return () =>
        navigateWithState(
          'unavailable',
          {
            progress: 4,
            type: 'us_tax_person',
            previousPathName: 'tax-details',
          },
          true,
          false
        );
    }

    if (!isSoleAccountOwner && hasBankDetailsFields) {
      return () =>
        navigateWithState('unavailable', {
          progress: 5,
          type: 'nominee',
          previousPathName: 'bank-details',
        });
    }

    const pageCriteriaAndNavigation = pagesCriteriaAndNavigation.find((x) =>
      x.requiredFields.some((field) => {
        const fieldValue = get(user, field);

        return isEmpty(fieldValue) && fieldValue !== false;
      })
    );

    if (pageCriteriaAndNavigation) {
      return () =>
        navigateWithState(
          pageCriteriaAndNavigation.route,
          {
            ...pageCriteriaAndNavigation.navigateProps.state,
          },
          true,
          pageCriteriaAndNavigation.navigateProps.preventScrollReset
        );
    }

    if (!hasCompletedQuestionnaire) {
      return () =>
        navigateWithState('questionnaire', {
          progress: 6,
        });
    }

    if (isKYCVerified) {
      return () =>
        navigateWithState('progress', {
          completedSteps: [
            ProgressSteps.Details,
            ProgressSteps.IdentityVerification,
          ],
          progress: 8,
        });
    }
    if (!hasPersonalDetailsLocked && !isKYCVerified) {
      return () =>
        navigateWithState('investment-profile-info', {
          progress: 7,
        });
    }

    if (!isKYCVerified) {
      return () =>
        navigateWithState('progress', {
          completedSteps: [ProgressSteps.Details],
          progress: 6,
        });
    }

    return () =>
      navigateWithState('profile', {
        progress: 1,
      });
  }
};
